import { useEffect, useMemo, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  Box,
  Button,
  ColumnLayout,
  Container,
  ExpandableSection,
  Header,
  Link,
  Modal,
  SpaceBetween,
  Spinner,
  Table,
} from '@amzn/awsui-components-console';
import {
  Group,
  GetGroupCommand,
  GetGroupCommandOutput,
  GetEntityBuildRequestInfoCommand,
  DeleteGroupCommand,
  RemoveTasksFromGroupCommand,
  UpdateGroupPackageBuildInfoCommand,
  EntityType,
} from '@amzn/awsdev-docs-virtual-smiley-conference-typescript-client';
import { useAuthState } from '../authentication/context';
import {
  useNavDispatch,
  overrideDetailBreadcrumb,
} from '../components/navigation/context';
import { ApiErrorProvider, useApiErrors } from '../components/ApiErrorContext';
import { ApiErrorNotifications } from '../components/ApiErrorNotifications';
import { callApi } from '../client';
import { WorkStatusBadge } from '../ConferencePages/conferenceInfo/ui/WorkStatusBadge';
import { Packages } from '../ConferencePages/conferenceInfo/ui/PackagesEditor/Packages';
import { AuditLog } from '../ConferencePages/conferenceInfo/ui/AuditLog';
import { BuildIdsMapComponent } from '../ConferencePages/conferenceInfo/ui/BuildIdsMapComponent';
import { convertToPT } from '../ConferencePages/conferenceInfo/utils';
import { RenameGroupModal } from './RenameGroupModal';
import { RemoveTasksFromGroupModal } from './RemoveTasksFromGroupModal';
import { AddTasksToGroupModal } from './AddTasksToGroupModal';
import { CreateGroupModal } from './CreateGroupModal';
import { AskFusionAssistantButton } from '../fusionAssistant/AskFusionAssistantButton';
import { TERMINAL_STATUSES } from '../utils/statusFilters';

function GroupDetailContent() {
  const { id } = useParams<{ id: string }>();
  const groupId = id;
  const history = useHistory();
  const navDispatch = useNavDispatch();
  const { token, userAlias, userGroups } = useAuthState();
  const { pushError } = useApiErrors();

  const [group, setGroup] = useState<Group | null>(null);
  const [buildRequestStatus, setBuildRequestStatus] = useState<
    string | undefined
  >(undefined);
  const [buildRequestId, setBuildRequestId] = useState<string | undefined>(
    undefined
  );
  const [loading, setLoading] = useState(true);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [renameModalVisible, setRenameModalVisible] = useState(false);
  const [removeTasksModalVisible, setRemoveTasksModalVisible] = useState(false);
  const [addTasksModalVisible, setAddTasksModalVisible] = useState(false);
  const [createGroupModalVisible, setCreateGroupModalVisible] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Stable task object for the Packages component. Built with useMemo so its reference only changes
  // when the underlying group data changes — NOT on every re-render (e.g. one triggered by pushError).
  // An inline literal here re-created a new reference each render, which fired the Packages
  // `useEffect([props.task])` reset and discarded the writer's unsaved in-progress package rows
  // (e.g. after a failed "Pull Tip of Branch"). Mirrors how task pages pass a stable LaunchTask.
  const packagesTask = useMemo(
    () =>
      group
        ? ({
            launchId: group.groupId,
            packageBuildInfoList: group.packageBuildInfoList ?? [],
            workStatus: group.workStatus,
            buildRequestStatus,
            buildRequestId,
            buildIdsMap: group.buildIdsMap,
            reviewEndpoint: group.reviewEndpoint,
            sharesPackageAcrossVenues: group.sharesPackageAcrossVenues,
          } as any)
        : null,
    [group, buildRequestStatus, buildRequestId]
  );

  useEffect(() => {
    void fetchGroup();
  }, [groupId, token]);

  async function fetchGroup() {
    if (!token) return;
    setLoading(true);

    try {
      const response: GetGroupCommandOutput = await callApi(
        new GetGroupCommand({ groupId }),
        token
      );

      if (response.$metadata.httpStatusCode === 200 && response.group) {
        setGroup(response.group);
        overrideDetailBreadcrumb(
          navDispatch,
          `/groups/view-${id}`,
          response.group.groupName
        );
        // Fetch initial build request info so BuildStatusBadge renders immediately
        try {
          const buildInfo = await callApi(
            new GetEntityBuildRequestInfoCommand({
              entityId: groupId,
              entityType: 'GROUP',
            }),
            token
          );
          if (buildInfo.$metadata.httpStatusCode === 200) {
            setBuildRequestStatus(
              buildInfo.buildRequestInfo.buildRequestStatus
            );
            setBuildRequestId(buildInfo.buildRequestInfo.buildRequestId);
          }
        } catch {
          // Non-critical — polling will pick it up
        }
      }
    } catch (err: any) {
      if (err?.statusCode === 404) {
        pushError('Group not found', 'This group no longer exists.');
        history.push('/groups');
        return;
      }
      pushError('Failed to load group', err?.message || String(err));
    } finally {
      setLoading(false);
    }
  }

  // A group is terminal (finished) once RESOLVED or SHIPPED. Reuses the shared TERMINAL_STATUSES set
  // so this stays in lockstep with the list's Resolved-view / default-hide semantics
  // (statusFilters.matchesStatusView) rather than re-listing the statuses here (finding on
  // CR-301380769).
  const isTerminal = group
    ? TERMINAL_STATUSES.has(group.workStatus ?? '')
    : false;
  const userIsCoordinator = userGroups?.includes('tcx-launch-admin') ?? false;
  const hasWriteAccess = group
    ? (group.writeAcl?.includes(userAlias ?? '') ?? false) || userIsCoordinator
    : false;

  const handleDelete = async () => {
    if (!token || !group) return;
    setDeleting(true);

    try {
      await callApi(new DeleteGroupCommand({ groupId }), token);
      history.push('/groups');
    } catch (err: any) {
      pushError('Delete Group Failed', err?.message || String(err));
    } finally {
      setDeleting(false);
      setDeleteModalVisible(false);
    }
  };

  const [removeConfirmTaskId, setRemoveConfirmTaskId] = useState<string | null>(
    null
  );

  const handleRemoveTask = async (launchId: string) => {
    if (!token || !group) return;
    setRemoveConfirmTaskId(null);

    try {
      const response = await callApi(
        new RemoveTasksFromGroupCommand({ groupId, launchIds: [launchId] }),
        token
      );

      if (response.group) {
        setGroup(response.group);
      } else {
        history.push({
          pathname: '/groups',
          state: {
            banner: 'Group deleted automatically — all tasks were removed.',
          },
        });
      }
    } catch (err: any) {
      pushError('Remove Task Failed', err?.message || String(err));
    }
  };

  if (loading) {
    return (
      <Box textAlign='center' padding={{ top: 'xxl' }}>
        <Spinner size='large' />
      </Box>
    );
  }

  if (!group) {
    return null;
  }

  return (
    <SpaceBetween size='l'>
      {/* Details panel */}
      <Container
        header={
          <Header
            variant='h2'
            actions={
              <SpaceBetween direction='horizontal' size='s'>
                {/* Add tasks — visible when hasWriteAccess && !isTerminal */}
                {hasWriteAccess && !isTerminal && (
                  <Button
                    onClick={() => setAddTasksModalVisible(true)}
                    data-test-id='add-tasks-to-group-button'
                  >
                    Add tasks to group
                  </Button>
                )}
                {/* Remove tasks — visible when hasWriteAccess && !isTerminal && has members */}
                {hasWriteAccess &&
                  !isTerminal &&
                  (group.memberTaskSummaries?.length ?? 0) >= 1 && (
                    <Button
                      onClick={() => setRemoveTasksModalVisible(true)}
                      data-test-id='remove-tasks-from-group-button'
                    >
                      Remove tasks from group
                    </Button>
                  )}
                {/* Rename — visible when hasWriteAccess && !isTerminal */}
                {hasWriteAccess && !isTerminal && (
                  <Button
                    onClick={() => setRenameModalVisible(true)}
                    data-test-id='rename-group-button'
                  >
                    Rename group
                  </Button>
                )}
                {/* Delete — visible when hasWriteAccess (NOT gated on !isTerminal) */}
                {hasWriteAccess && (
                  <Button
                    onClick={() => setDeleteModalVisible(true)}
                    data-test-id='delete-group-button'
                  >
                    Delete group
                  </Button>
                )}
                {/* Ask Fusion Assistant — available to any viewer */}
                <AskFusionAssistantButton
                  dataTestId='ask-fusion-assistant-group-button'
                  context={{
                    groupId: group.groupId,
                    groupName: group.groupName,
                    packages: (group.packageBuildInfoList ?? []).map((pkg) => ({
                      packageName: pkg.packageName ?? '',
                      commitId: pkg.commitId,
                    })),
                  }}
                />
              </SpaceBetween>
            }
          >
            {group.groupName}
          </Header>
        }
      >
        <ColumnLayout columns={4} variant='text-grid'>
          <div>
            <Box variant='awsui-key-label'>Group ID</Box>
            <Box>
              <span style={{ fontFamily: 'monospace' }}>{group.groupId}</span>
            </Box>
          </div>
          <div>
            <Box variant='awsui-key-label'>Conference</Box>
            <Box>{group.conference}</Box>
          </div>
          <div>
            <Box variant='awsui-key-label'>Venue</Box>
            <Box>{group.venue}</Box>
          </div>
          <div>
            <Box variant='awsui-key-label'>Status</Box>
            <WorkStatusBadge status={group.workStatus} />
          </div>
          <div>
            <Box variant='awsui-key-label'>Member tasks</Box>
            <Box>{group.memberTaskSummaries?.length ?? 0}</Box>
          </div>
          <div>
            <Box variant='awsui-key-label'>Created</Box>
            <Box>{convertToPT(group.createDate)}</Box>
          </div>
          <div>
            <Box variant='awsui-key-label'>Last updated</Box>
            <Box>{convertToPT(group.lastUpdatedDate)}</Box>
          </div>
        </ColumnLayout>
      </Container>

      {/* Member tasks table */}
      <Table
        header={
          <Header counter={`(${group.memberTaskSummaries?.length ?? 0})`}>
            Member tasks
          </Header>
        }
        columnDefinitions={[
          {
            id: 'name',
            header: 'Name',
            cell: (item) => (
              <Link href={`#/conferences/view-${item.launchId}`}>
                {item.name}
              </Link>
            ),
          },
          {
            id: 'launchId',
            header: 'Launch ID',
            cell: (item) => (
              <span style={{ fontFamily: 'monospace' }}>{item.launchId}</span>
            ),
          },
          {
            id: 'actions',
            header: 'Actions',
            cell: (item) =>
              hasWriteAccess && !isTerminal ? (
                <Button
                  variant='inline-link'
                  onClick={() => setRemoveConfirmTaskId(item.launchId)}
                >
                  Remove
                </Button>
              ) : null,
          },
        ]}
        items={group.memberTaskSummaries ?? []}
        trackBy='launchId'
      />

      {/* Doc packages — Packages component with group callbacks */}
      <Container header={<Header variant='h2'>Doc packages</Header>}>
        <Packages
          task={packagesTask}
          editable={true}
          entityType={EntityType.GROUP}
          onSubmitPackageBuildInfo={async (packageBuildInfoList) => {
            if (!token)
              throw new Error('Authentication token is not available');
            return callApi(
              new UpdateGroupPackageBuildInfoCommand({
                groupId,
                packageBuildInfoList,
              }),
              token
            );
          }}
          onPollStatus={async () => {
            if (!token) throw new Error('No token');
            return callApi(
              new GetEntityBuildRequestInfoCommand({
                entityId: groupId,
                entityType: 'GROUP',
              }),
              token
            );
          }}
          onPoll={(newWorkStatus) => {
            // Only update group state when workStatus actually changes. Otherwise every poll
            // creates a new group reference, which churns the useMemo'd packagesTask and fires
            // Packages' useEffect([props.task]) — that resets `items` from `group.packageBuildInfoList`
            // and discards either (a) the writer's locally-edited rows pre-submit, or (b) the
            // packageBuildInfoList just persisted server-side after a validation failure (which
            // the in-memory group hasn't refetched yet).
            setGroup((prev) => {
              if (!prev || prev.workStatus === newWorkStatus) return prev;
              return { ...prev, workStatus: newWorkStatus };
            });
          }}
        />
      </Container>

      {/* Audit log */}
      <ExpandableSection
        variant='container'
        defaultExpanded={false}
        headerText='Audit Log'
        headerDescription='Displays recent changes to this group (up to 10).'
      >
        <AuditLog auditLog={group.auditLog} />
      </ExpandableSection>

      {/* Delete confirmation modal */}
      <Modal
        visible={deleteModalVisible}
        onDismiss={() => setDeleteModalVisible(false)}
        header='Delete group?'
        footer={
          <Box float='right'>
            <SpaceBetween direction='horizontal' size='xs'>
              <Button
                variant='link'
                onClick={() => setDeleteModalVisible(false)}
              >
                Cancel
              </Button>
              <Button
                variant='primary'
                onClick={handleDelete}
                loading={deleting}
                data-test-id='confirm-delete-group-button'
              >
                Delete
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <Box>
          Are you sure you want to delete <b>{group.groupName}</b>? This will
          permanently delete the group and reset all{' '}
          <b>{group.memberTaskSummaries?.length ?? 0} member task(s)</b> to{' '}
          <b>Task Received</b> status, clearing their doc packages. This action
          cannot be undone.
        </Box>
      </Modal>

      <RenameGroupModal
        visible={renameModalVisible}
        groupId={groupId}
        currentName={group.groupName}
        onDismiss={() => setRenameModalVisible(false)}
        onSuccess={() => {
          setRenameModalVisible(false);
          void fetchGroup();
        }}
      />

      <RemoveTasksFromGroupModal
        visible={removeTasksModalVisible}
        groupId={groupId}
        groupName={group.groupName}
        memberTaskSummaries={group.memberTaskSummaries ?? []}
        onDismiss={() => setRemoveTasksModalVisible(false)}
        onSuccess={(groupDeleted) => {
          setRemoveTasksModalVisible(false);
          if (groupDeleted) {
            history.push({
              pathname: '/groups',
              state: {
                banner: 'Group deleted automatically — all tasks were removed.',
              },
            });
          } else {
            void fetchGroup();
          }
        }}
      />

      {addTasksModalVisible && (
        <AddTasksToGroupModal
          visible={addTasksModalVisible}
          conference={group.conference}
          venue={group.venue}
          preSelectedGroupId={groupId}
          onDismiss={() => setAddTasksModalVisible(false)}
          onSuccess={() => {
            setAddTasksModalVisible(false);
            void fetchGroup();
          }}
          onSwitchToCreate={() => {
            setAddTasksModalVisible(false);
            setCreateGroupModalVisible(true);
          }}
        />
      )}

      <CreateGroupModal
        visible={createGroupModalVisible}
        onDismiss={() => setCreateGroupModalVisible(false)}
        onSuccess={(newGroupId) => {
          setCreateGroupModalVisible(false);
          history.push(`/groups/view-${newGroupId}`);
        }}
      />

      {/* Per-row remove confirmation */}
      <Modal
        visible={removeConfirmTaskId !== null}
        onDismiss={() => setRemoveConfirmTaskId(null)}
        header='Remove task from group?'
        footer={
          <Box float='right'>
            <SpaceBetween direction='horizontal' size='xs'>
              <Button
                variant='link'
                onClick={() => setRemoveConfirmTaskId(null)}
              >
                Cancel
              </Button>
              <Button
                variant='primary'
                onClick={() =>
                  removeConfirmTaskId && handleRemoveTask(removeConfirmTaskId)
                }
              >
                Remove
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size='s'>
          <Box>
            Removing this task from the group will reset it to 'Task Received'
            status and clear its doc packages. This action cannot be undone.
          </Box>
        </SpaceBetween>
      </Modal>
    </SpaceBetween>
  );
}

export function GroupDetail() {
  return (
    <ApiErrorProvider>
      <div>
        <ApiErrorNotifications />
        <GroupDetailContent />
      </div>
    </ApiErrorProvider>
  );
}

export default GroupDetail;
